import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MessageBox, Spacing, Text } from '@reservamos/elements';
import { useSelector } from 'react-redux';
import { selectCardCheckoutDecision } from 'selectors/cardCheckoutSelectors';
import { shouldUseAdyen } from 'utils/adyen';
import { shouldUseCybersource } from 'utils/Cybersource';
import YunoForm from '../YunoForm';
import CardForm from '../CardForm';
import PaypalForm from '../PaypalForm';
import PaypalV6Form from '../PaypalV6Form';
import KueskipayForm from '../KueskipayForm';
import AgencyForm from '../AgencyForm';
import StoreForm from '../StoreForm';
import ThirdPartyForm from '../ThirdPartyForm';
import AdyenForm from '../AdyenForm';
import StripeForm from '../StripeForm';
import PixForm from '../PixForm';
import FreePayForm from '../FreePayForm';
import ReservamosPayForm from '../ReservamosPayForm';
import SavedCards from '../SavedCards';
import ExchangePayForm from '../ExchangePayForm';
import MercadoPagoTDCForm from '../MercadoPagoTDCForm';
import availablePaymentEngines from '../../../constants/PaymentEngine';
import EfectyForm from '../EfectyForm/EfectyForm';
import CybersourceForm from '../CybersourceForm';
import TransferFormStrategy from './TransferFormStrategy';
import EvertecForm from '../EvertecForm';
import CieloTDCForm from '../CieloTDCForm';
import AplazoForm from '../AplazoForm';
import MercadoPagoForm from '../MercadoPagoForm';
import YapeForm from '../YapeForm/YapeForm';
import { isPayPalV6Enabled } from '../../../utils/paypalV6Config';
import AdyenApplePayForm from './ApplePay/AdyenApplePayForm';
import StripeApplePayForm from './ApplePay/StripeApplePayForm';
import AdyenGooglePayForm from './GooglePay/AdyenGooglePayForm';
import StripeGooglePayForm from './GooglePay/StripeGooglePayForm';
import { BasisTheoryCheckout } from '../BasisTheoryCheckout';

/**
 * PaymentForm component renders the appropriate payment form based on the selected payment method.
 * It supports various payment methods like credit card, PayPal, agency pay, third party, etc.
 * The component also handles the display of error messages and saved cards when necessary.
 *
 * @param {Object} props - The props for the PaymentForm component.
 * @param {string} props.formType - The type of payment form to display.
 * @param {Function} props.submitCard - The callback function to submit credit card form.
 * @param {Function} props.submitCardFail - The callback function when credit card form submission fails.
 * @param {Function} props.submitTransfer - The callback function to submit transfer form.
 * @param {Function} props.onYapeFormDataChange - Callback function when Yape form data changes.
 * @param {Function} props.onYapeValidationChange - Callback function when Yape validation changes.
 * @param {boolean} props.isYapeSubmitting - Flag indicating if Yape form is submitting.
 * @param {string} props.selectedOption - The currently selected payment option.
 * @param {boolean} props.cardErrorOccured - Flag indicating if there was an error with credit card payment.
 * @param {boolean} props.showSavedCards - Flag indicating if saved cards should be displayed.
 * @param {boolean} props.showAdyenForm - Flag indicating if the Adyen form should be displayed.
 * @param {string} props.paymentEngine - The payment engine being used.
 * @param {boolean} props.showCheckbox - Flag indicating if the checkbox should be displayed.
 * @param {number} props.purchaseTotal - The total amount of the purchase.
 * @param {boolean} props.isExchange - Flag indicating if the payment is for an exchange.
 * @param {Function} props.onPaymentButtonClick - Callback function to handle payment button click with payment parameters.
 * @returns {React.Component} The PaymentForm component.
 */
const PaymentForm = ({
  formType,
  submitCardFail,
  submitCard,
  submitTransfer,
  onYapeFormDataChange,
  onYapeValidationChange,
  isYapeSubmitting,
  selectedOption,
  cardErrorOccured,
  showSavedCards,
  showAdyenForm,
  showCheckbox,
  paymentEngine,
  purchaseTotal,
  isExchange,
  onPaymentButtonClick,
}) => {
  const selectedPaymentMethod = useSelector((state) => state.purchase.get('selectedPaymentMethod'));
  const cardCheckoutMode = useSelector((state) => {
    return selectCardCheckoutDecision(state, selectedPaymentMethod).mode;
  });
  const basisTheoryIsActive = formType === 'credit_card' && cardCheckoutMode === 'basis_theory';
  const [hasMountedBasisTheory, setHasMountedBasisTheory] = useState(basisTheoryIsActive);
  const basisTheoryPublicKey = useSelector(
    (state) => state.whitelabelConfig.env.shared?.basisTheory?.publicKey || '',
  );
  const { t } = useTranslation(['purchase', 'payment']);
  const { enabled: mercadoPagoIsEnabled, creditCard: mercadoPagoTDC } = useSelector(
    (state) => state.whitelabelConfig.env.mercadoPago,
  );
  const mercadoPagoCreditCardIsEnabled = mercadoPagoIsEnabled && mercadoPagoTDC?.enabled;
  const {
    enabled: cieloIsEnabled,
    enableThreeDS,
    enableSilentOrderPost,
  } = useSelector((state) => state.whitelabelConfig.env.cielo);

  const shouldUseMercadoPago =
    mercadoPagoIsEnabled && paymentEngine === availablePaymentEngines.mercadoPago;
  const shouldUseCielo =
    cieloIsEnabled &&
    (enableThreeDS || enableSilentOrderPost) &&
    paymentEngine === availablePaymentEngines.cielo;
  const shouldUseEvertec = selectedPaymentMethod?.engine === 'evertec';
  const shouldUseYuno = selectedPaymentMethod?.provider === 'yuno';
  const shouldUseStripe = selectedPaymentMethod?.engine === 'stripe';

  useEffect(() => {
    if (basisTheoryIsActive) setHasMountedBasisTheory(true);
  }, [basisTheoryIsActive]);

  if (shouldUseYuno) formType = 'yuno';

  let form;

  switch (formType) {
    case 'credit_card':
      form = (
        <div className="mt-5">
          <Spacing vertical>
            <CardForm onSubmit={submitCard} onSubmitFail={submitCardFail} />
          </Spacing>
        </div>
      );

      if (shouldUseCybersource(paymentEngine)) {
        form = (
          <Spacing vertical>
            <CybersourceForm onSubmit={submitCard} onSubmitFail={submitCardFail} />
          </Spacing>
        );
      }

      if (shouldUseAdyen(paymentEngine)) {
        form = (
          <div className="adyen-form">
            <Spacing vertical>
              {showSavedCards && <SavedCards />}
              <AdyenForm showAdyenForm={showAdyenForm} showCheckbox={showCheckbox} />
              {cardErrorOccured && (
                <MessageBox title={t('label.problems_paying')} borderColor="warning">
                  <Text size="S">{t('text.suggestions_to_payment_problems')}</Text>
                </MessageBox>
              )}
            </Spacing>
          </div>
        );
      }

      if (shouldUseMercadoPago && mercadoPagoCreditCardIsEnabled) {
        form = <MercadoPagoTDCForm amount={purchaseTotal} />;
      }

      if (shouldUseEvertec) {
        form = <EvertecForm />;
      }

      if (shouldUseCielo) {
        form = <CieloTDCForm />;
      }

      if (shouldUseStripe) {
        form = (
          <Spacing vertical>
            <StripeForm />
            {cardErrorOccured && (
              <MessageBox title={t('label.problems_paying')} borderColor="warning">
                <Text size="S">{t('text.suggestions_to_payment_problems')}</Text>
              </MessageBox>
            )}
          </Spacing>
        );
      }
      break;
    case 'paypal':
      // Use PaypalV6Form if v6 is enabled, otherwise use legacy PaypalForm
      form = isPayPalV6Enabled() ? (
        <PaypalV6Form onPaymentButtonClick={onPaymentButtonClick} />
      ) : (
        <PaypalForm />
      );
      break;
    case 'apple_pay':
      // Dynamic component selection based on engine
      if (selectedPaymentMethod?.engine === 'adyen') {
        form = <AdyenApplePayForm onPaymentButtonClick={onPaymentButtonClick} />;
      } else if (selectedPaymentMethod?.engine === 'stripe') {
        form = <StripeApplePayForm onPaymentButtonClick={onPaymentButtonClick} />;
      }
      break;
    case 'google_pay':
      if (selectedPaymentMethod?.engine === 'adyen') {
        form = <AdyenGooglePayForm onPaymentButtonClick={onPaymentButtonClick} />;
      } else if (selectedPaymentMethod?.engine === 'stripe') {
        form = <StripeGooglePayForm onPaymentButtonClick={onPaymentButtonClick} />;
      }
      break;
    case 'agency_pay':
      form = <AgencyForm />;
      break;
    case 'third_party':
      form = <ThirdPartyForm />;
      break;
    case 'reservamos_pay':
      form = <ReservamosPayForm />;
      break;
    case 'exchange':
      form = <ExchangePayForm />;
      break;
    case 'free_pay':
      form = <FreePayForm isExchange={isExchange} />;
      break;
    case 'transfer':
      form = (
        <TransferFormStrategy
          selectedPaymentMethod={selectedPaymentMethod}
          submitTransfer={submitTransfer}
        />
      );
      break;
    case 'store':
      form = <StoreForm />;
      break;
    case 'bnpl':
      if (selectedPaymentMethod?.provider === 'aplazo') {
        form = <AplazoForm />;
      } else {
        form = <KueskipayForm />;
      }
      break;
    case 'otp':
      form = (
        <YapeForm
          onFormDataChange={onYapeFormDataChange}
          onValidationChange={onYapeValidationChange}
          isSubmitting={isYapeSubmitting}
        />
      );
      break;
    case 'wallet':
      form = <MercadoPagoForm />;
      break;
    case 'pix':
      form = <PixForm />;
      break;
    case 'efecty':
      form = <EfectyForm />;
      break;
    case 'yuno':
      form = <YunoForm engine={selectedPaymentMethod?.engine} />;
      break;
    default:
      form = <div />;
  }

  if (basisTheoryIsActive || hasMountedBasisTheory) {
    const alternateCardForm = form;
    form = (
      <>
        <div className={basisTheoryIsActive ? '' : 'hidden'} aria-hidden={!basisTheoryIsActive}>
          <BasisTheoryCheckout
            active={basisTheoryIsActive}
            cardErrorOccured={cardErrorOccured}
            publicKey={basisTheoryPublicKey}
          />
        </div>
        {!basisTheoryIsActive && alternateCardForm}
      </>
    );
  }

  const displayForm = selectedOption === formType || formType === 'yuno';

  // Use a different id for PayPal to avoid conflict with window.paypal
  // Some browsers expose DOM elements with id="paypal" on window.paypal,
  // which can interfere with the PayPal SDK
  const elementId = formType === 'paypal' && isPayPalV6Enabled() ? 'paypal-form' : formType;

  return (
    <div className="tab-content mt-3" id={elementId}>
      {displayForm && form}
    </div>
  );
};

PaymentForm.propTypes = {
  formType: PropTypes.string.isRequired,
  selectedOption: PropTypes.string.isRequired,
  submitCard: PropTypes.func,
  submitCardFail: PropTypes.func,
  submitTransfer: PropTypes.func.isRequired,
  onYapeFormDataChange: PropTypes.func,
  onYapeValidationChange: PropTypes.func,
  isYapeSubmitting: PropTypes.bool,
  cardErrorOccured: PropTypes.bool,
  showSavedCards: PropTypes.bool,
  showAdyenForm: PropTypes.bool,
  paymentEngine: PropTypes.string,
  showCheckbox: PropTypes.bool,
  purchaseTotal: PropTypes.number.isRequired,
  isExchange: PropTypes.bool.isRequired,
  onPaymentButtonClick: PropTypes.func,
};

PaymentForm.defaultProps = {
  cardErrorOccured: false,
  showSavedCards: false,
  showAdyenForm: false,
  showCheckbox: true,
};

export default PaymentForm;
